/**
 * Timeline Event Utilities
 *
 * Pure helper functions for working with events in timeline views.
 * Handles grouping events by date, determining if a date has events,
 * and formatting event displays for timeline integration.
 */

import type { EventResponse, EventType, UserMedicationResponse } from "@floline/api-client";
import { formatLocalDateYmd } from "@floline/journal-aggregation";

// ============================================================================
// Constants
// ============================================================================

/**
 * Medication event types that historically represented ongoing medications.
 *
 * Since T2.8 this set is intentionally empty — medication duration spans are
 * now sourced from `user_medications` rows instead of events.
 * `medication_start` and `medication_dose_change` events are treated as
 * point-in-time markers.
 */
const ONGOING_MEDICATION_TYPES: ReadonlySet<string> = new Set([
  // Intentionally empty — see comment above.
]);

/**
 * Get today's date in YYYY-MM-DD format (local timezone)
 */
function getTodayDate(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * Check if an event type represents an ongoing medication
 */
export function isOngoingMedicationType(eventType: string): boolean {
  return ONGOING_MEDICATION_TYPES.has(eventType);
}

/**
 * Get the effective end date for an event.
 * For ongoing medication events (medication_start/dose_change with no endDate),
 * returns today's date so they render as spans extending to the present.
 */
export function getEffectiveEndDate(event: TimelineEventData): string | null {
  if (event.endDate) return event.endDate;
  if (isOngoingMedicationType(event.eventType)) return getTodayDate();
  return null;
}

// ============================================================================
// Types
// ============================================================================

/**
 * Simplified event data for timeline display
 */
export interface TimelineEventData {
  /** Event ID */
  id: string;
  /** Event type for icon selection */
  eventType: EventType;
  /** Event title */
  title: string;
  /** Start date in YYYY-MM-DD format */
  startDate: string;
  /** End date in YYYY-MM-DD format (for duration events) */
  endDate: string | null;
  /** Parent event ID for medication lifecycle tracking */
  parentEventId: string | null;
}

/**
 * Map of date strings to arrays of events for that date
 */
export type EventsByDate = Record<string, TimelineEventData[]>;

// ============================================================================
// Core Functions
// ============================================================================

/**
 * Convert an EventResponse to simplified TimelineEventData
 *
 * @param event - Full event response from API
 * @returns Simplified event data for timeline display
 * @example
 * const timelineEvent = toTimelineEventData(apiEvent);
 */
export function toTimelineEventData(event: EventResponse): TimelineEventData {
  return {
    id: event.id,
    eventType: event.eventType,
    title: event.title,
    startDate: event.startDate,
    endDate: event.endDate,
    parentEventId: event.parentEventId,
  };
}

/**
 * Group events by their start date for quick lookup
 *
 * Events with duration (endDate) are added to each date they span.
 *
 * @param events - Array of events to group
 * @returns Object mapping date strings to arrays of events
 * @example
 * const grouped = groupEventsByDate(events);
 * const todayEvents = grouped['2026-01-11'] ?? [];
 */
export function groupEventsByDate(events: EventResponse[]): EventsByDate {
  const result: EventsByDate = {};

  for (const event of events) {
    const timelineEvent = toTimelineEventData(event);
    const effectiveEnd = getEffectiveEndDate(timelineEvent);

    // Add to start date
    if (!result[event.startDate]) {
      result[event.startDate] = [];
    }
    result[event.startDate].push(timelineEvent);

    // If event has duration, add to each date in the range
    if (effectiveEnd && effectiveEnd !== event.startDate) {
      const startDate = new Date(`${event.startDate}T00:00:00`);
      const endDate = new Date(`${effectiveEnd}T00:00:00`);

      // Add to each intermediate and end date
      const currentDate = new Date(startDate);
      currentDate.setDate(currentDate.getDate() + 1); // Start from day after start

      while (currentDate <= endDate) {
        const dateStr = formatLocalDateYmd(currentDate);
        if (!result[dateStr]) {
          result[dateStr] = [];
        }
        // Only add if not already in the array (avoid duplicates)
        if (!result[dateStr].some((e) => e.id === timelineEvent.id)) {
          result[dateStr].push(timelineEvent);
        }
        currentDate.setDate(currentDate.getDate() + 1);
      }
    }
  }

  return result;
}

/**
 * Check if a specific date has any events
 *
 * @param date - Date string in YYYY-MM-DD format
 * @param eventsByDate - Pre-grouped events lookup
 * @returns true if the date has one or more events
 * @example
 * const hasEvents = dateHasEvents('2026-01-11', groupedEvents);
 */
export function dateHasEvents(
  date: string,
  eventsByDate: EventsByDate
): boolean {
  return (eventsByDate[date]?.length ?? 0) > 0;
}

/**
 * Get events for a specific date
 *
 * @param date - Date string in YYYY-MM-DD format
 * @param eventsByDate - Pre-grouped events lookup
 * @returns Array of events for that date (empty if none)
 * @example
 * const todayEvents = getEventsForDate('2026-01-11', groupedEvents);
 */
export function getEventsForDate(
  date: string,
  eventsByDate: EventsByDate
): TimelineEventData[] {
  return eventsByDate[date] ?? [];
}

/**
 * Get the count of events for a specific date
 *
 * @param date - Date string in YYYY-MM-DD format
 * @param eventsByDate - Pre-grouped events lookup
 * @returns Number of events on that date
 * @example
 * const count = getEventCountForDate('2026-01-11', groupedEvents);
 */
export function getEventCountForDate(
  date: string,
  eventsByDate: EventsByDate
): number {
  return eventsByDate[date]?.length ?? 0;
}

// ============================================================================
// Date Range Functions
// ============================================================================

/**
 * Get the date range from an array of daily records
 *
 * @param recordDates - Array of date strings in YYYY-MM-DD format
 * @returns Object with startDate and endDate, or null if empty
 * @example
 * const range = getDateRangeFromRecords(['2026-01-05', '2026-01-10', '2026-01-01']);
 * // { startDate: '2026-01-01', endDate: '2026-01-10' }
 */
export function getDateRangeFromRecords(
  recordDates: string[]
): { startDate: string; endDate: string } | null {
  if (recordDates.length === 0) {
    return null;
  }

  const sorted = [...recordDates].sort();
  return {
    startDate: sorted[0],
    endDate: sorted[sorted.length - 1],
  };
}

/**
 * Expand date range to include buffer days for events that span boundaries
 *
 * @param startDate - Start date in YYYY-MM-DD format
 * @param endDate - End date in YYYY-MM-DD format
 * @param bufferDays - Number of days to add on each end (default: 7)
 * @returns Expanded date range
 * @example
 * const expanded = expandDateRange('2026-01-05', '2026-01-15');
 * // { startDate: '2025-12-29', endDate: '2026-01-22' }
 */
export function expandDateRange(
  startDate: string,
  endDate: string,
  bufferDays = 7
): { startDate: string; endDate: string } {
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);

  start.setDate(start.getDate() - bufferDays);
  end.setDate(end.getDate() + bufferDays);

  return {
    startDate: formatLocalDateYmd(start),
    endDate: formatLocalDateYmd(end),
  };
}

// ============================================================================
// Display Formatting
// ============================================================================

/**
 * Format event count for display (e.g., "+2 events")
 *
 * @param count - Number of events
 * @returns Formatted string or empty string if count is 0
 * @example
 * formatEventCount(1) // "1 event"
 * formatEventCount(3) // "3 events"
 * formatEventCount(0) // ""
 */
export function formatEventCount(count: number): string {
  if (count === 0) return "";
  if (count === 1) return "1 event";
  return `${count} events`;
}

/**
 * Get a short summary of events for a date (e.g., first event title)
 *
 * @param events - Array of events for a date
 * @param maxLength - Maximum length of the summary (default: 30)
 * @returns Summary string
 * @example
 * getEventsSummary([{title: 'Started Ibuprofen', ...}]) // "Started Ibuprofen"
 * getEventsSummary([{title: 'Started Ibuprofen', ...}, {title: 'Physio session', ...}]) // "Started Ibuprofen +1"
 */
export function getEventsSummary(
  events: TimelineEventData[],
  maxLength = 30
): string {
  if (events.length === 0) return "";

  const firstEvent = events[0];
  let summary = firstEvent.title;

  // Truncate if too long
  if (summary.length > maxLength) {
    summary = `${summary.slice(0, maxLength - 3)}...`;
  }

  // Add count if more than one event
  if (events.length > 1) {
    summary = `${summary} +${events.length - 1}`;
  }

  return summary;
}

/**
 * Check if an event is a duration event (has end date different from start,
 * or is an ongoing medication without endDate)
 *
 * @param event - Event to check
 * @returns true if the event spans multiple days
 * @example
 * isDurationEvent({ startDate: '2026-01-01', endDate: '2026-01-07', ... }) // true
 * isDurationEvent({ startDate: '2026-01-01', endDate: null, eventType: 'medication_start', ... }) // true (ongoing)
 * isDurationEvent({ startDate: '2026-01-01', endDate: null, eventType: 'custom', ... }) // false
 */
export function isDurationEvent(event: TimelineEventData): boolean {
  const effectiveEnd = getEffectiveEndDate(event);
  return effectiveEnd !== null && effectiveEnd !== event.startDate;
}

/**
 * Determine if a date is the start, middle, or end of a duration event
 *
 * @param date - Date string in YYYY-MM-DD format
 * @param event - The duration event
 * @returns 'start' | 'middle' | 'end' | null (null if not in range)
 * @example
 * const position = getDatePositionInEvent('2026-01-03', event);
 */
export function getDatePositionInEvent(
  date: string,
  event: TimelineEventData
): "start" | "middle" | "end" | null {
  const effectiveEnd = getEffectiveEndDate(event);

  if (!effectiveEnd) {
    // Not a duration event
    return date === event.startDate ? "start" : null;
  }

  if (date === event.startDate) return "start";
  if (date === effectiveEnd) return "end";
  if (date > event.startDate && date < effectiveEnd) return "middle";

  return null;
}

/**
 * Information about a duration event for a specific date
 */
export interface DurationEventInfo {
  /** The event data */
  event: TimelineEventData;
  /** Position within the event: start, middle, or end */
  position: "start" | "middle" | "end";
  /** Whether this is an ongoing event (no explicit endDate, e.g. active medication) */
  isOngoing: boolean;
}

/**
 * Get duration events that include a specific date
 *
 * Returns only events that span multiple days and include the given date.
 * Each result includes the event and the position of the date within it.
 *
 * @param date - Date string in YYYY-MM-DD format
 * @param eventsByDate - Pre-grouped events lookup
 * @returns Array of duration event info, or empty array if none
 * @example
 * const durations = getDurationEventsForDate('2026-01-05', groupedEvents);
 * // [{ event: {...}, position: 'middle' }]
 */
export function getDurationEventsForDate(
  date: string,
  eventsByDate: EventsByDate
): DurationEventInfo[] {
  const eventsOnDate = eventsByDate[date] ?? [];
  const result: DurationEventInfo[] = [];

  for (const event of eventsOnDate) {
    if (!isDurationEvent(event)) continue;

    const position = getDatePositionInEvent(date, event);
    if (position) {
      result.push({ event, position, isOngoing: event.endDate === null && isOngoingMedicationType(event.eventType) });
    }
  }

  return result;
}

/**
 * Get the primary (first) duration event for a date, if any
 *
 * @param date - Date string in YYYY-MM-DD format
 * @param eventsByDate - Pre-grouped events lookup
 * @returns Primary duration event info, or null if none
 */
export function getPrimaryDurationEvent(
  date: string,
  eventsByDate: EventsByDate
): DurationEventInfo | null {
  const durations = getDurationEventsForDate(date, eventsByDate);
  return durations.length > 0 ? durations[0] : null;
}

// ============================================================================
// Medication-Based Duration Helpers (T2.8)
// ============================================================================

/**
 * Determine the duration event info for a date based on user_medications rows.
 *
 * All medications are considered regardless of `showInTrends` — that toggle
 * only affects the trends **chart overlays** (`filterChartOnlySpans`). For ongoing
 * medications (no endDate) the span extends to today.
 *
 * Returns the first matching medication's info, or null if no medication covers
 * the given date.
 *
 * The returned `DurationEventInfo.event` is a *synthesised* `TimelineEventData`
 * with `eventType: "medication_start"` so the existing colour mapping in
 * `DurationEventIndicator` gives it the Medication category (blue).
 *
 * @param date - Date string in YYYY-MM-DD format
 * @param medications - All user_medications rows
 * @returns DurationEventInfo for the first covering medication, or null
 */
export function getMedicationDurationForDate(
  date: string,
  medications: UserMedicationResponse[]
): DurationEventInfo | null {
  const today = getTodayDate();

  for (const med of medications) {
    const effectiveEnd = med.endDate ?? today;
    if (date < med.startDate || date > effectiveEnd) continue;

    // Determine position within the medication span
    let position: "start" | "middle" | "end";
    if (date === med.startDate) {
      position = "start";
    } else if (date === effectiveEnd) {
      position = "end";
    } else {
      position = "middle";
    }

    const isOngoing = med.endDate === null;

    // Synthesise a TimelineEventData so the existing DurationEventIndicator
    // component can render it without changes.
    const syntheticEvent: TimelineEventData = {
      id: `med-${med.id}`,
      eventType: "medication_start" as EventType,
      title: med.displayName,
      startDate: med.startDate,
      endDate: med.endDate,
      parentEventId: null,
    };

    return { event: syntheticEvent, position, isOngoing };
  }

  return null;
}
